import { gtmTrackEvent, gtmTrackConversion } from "./useGTM";

export const STEP_LABELS = ["Sprawdzenie", "Dane firmowe", "Konto", "Plan", "Podsumowanie"] as const;

/**
 * Error types for registration_error event — never raw API messages.
 *
 * MUST be opaque error type identifiers — NEVER raw user input or PII values.
 * Used in analytics events; values are visible to client.
 */
export type RegistrationErrorType =
  | "email_taken"
  | "nip_taken"
  | "phone_taken"
  | "api_unavailable"
  | "validation_failed"
  | "turnstile_failed"
  // 5b trial invite: `?invite=` present but not a token (damaged link)
  | "invite_malformed"
  // 5b trial invite: unknown, used or expired (pre-check or create-direct)
  | "invite_invalid"
  // 5b trial invite: bound to another e-mail (create-direct)
  | "invite_email_mismatch";

/** AC-9: closed enum for the registration scope — carries `?scheme=` end-to-end. */
export type GrantsScheme = "kfs" | "bur" | "all";

export interface RegistrationAnalytics {
  trackRegistrationStart: (params: {
    source: string | undefined | null;
    plan: string | undefined | null;
    signupEntry?: string | undefined | null;
    /** AC-9/TS-12: already resolved through the closed allowlist — never the raw query value. */
    scheme?: GrantsScheme;
  }) => void;
  trackStepView: (stepNumber: number) => void;
  trackStepComplete: (stepNumber: number) => void;
  trackEligibilityCheck: (params: {
    result: string;
    gusSuccess: boolean;
  }) => void;
  trackRegistrationSubmit: (params: {
    planId: string;
    planName: string;
    billingInterval: string;
    activationType: string;
  }) => void;
  trackRegistrationError: (params: {
    stepNumber: number;
    errorType: RegistrationErrorType;
  }) => void;
  trackSignUp: (params: {
    method: string;
    planId: string;
    useBeacon: boolean;
  }) => void;
}

/**
 * Removes the subdomain query parameter from a page path to prevent
 * tenant identity leakage in analytics (AC-11).
 */
export function sanitizePagePath(path: string): string {
  try {
    // Use URL with a dummy base to parse relative paths
    const url = new URL(path, "https://placeholder.invalid");
    url.searchParams.delete("subdomain");
    // Return pathname + sanitized search
    const search = url.searchParams.toString();
    return url.pathname + (search ? `?${search}` : "");
  } catch {
    return path;
  }
}

/**
 * Hook for registration funnel analytics events (Faza 2: events #2-8).
 *
 * Extracts analytics logic from Register.tsx (SRP — AC-12).
 * All events go through useGTM which enforces consent gating.
 *
 * Returns stable function references (no useCallback needed — functions are
 * defined at module scope and referenced directly).
 */
export const useRegistrationAnalytics = (): RegistrationAnalytics => {
  return {
    trackRegistrationStart,
    trackStepView,
    trackStepComplete,
    trackEligibilityCheck,
    trackRegistrationSubmit,
    trackRegistrationError,
    trackSignUp,
  };
};

const trackRegistrationStart = (params: {
  source: string | undefined | null;
  plan: string | undefined | null;
  signupEntry?: string | undefined | null;
  scheme?: GrantsScheme;
}): void => {
  gtmTrackEvent("registration_start", {
    source: params.source ?? undefined,
    plan: params.plan ?? undefined,
    // `signup_entry` = internal-link entry point (the landing `ref` param). NOT a
    // utm_source — utm_* on internal links resets the GA4 session/source attribution.
    signup_entry: params.signupEntry ?? undefined,
    scheme: params.scheme ?? undefined,
  });
};

const trackStepView = (stepNumber: number): void => {
  const stepName = STEP_LABELS[stepNumber - 1];
  gtmTrackEvent("registration_step_view", {
    step_number: stepNumber,
    step_name: stepName,
  });
};

const trackStepComplete = (stepNumber: number): void => {
  const stepName = STEP_LABELS[stepNumber - 1];
  gtmTrackEvent("registration_step_complete", {
    step_number: stepNumber,
    step_name: stepName,
  });
};

const trackEligibilityCheck = (params: {
  result: string;
  gusSuccess: boolean;
}): void => {
  gtmTrackEvent("registration_eligibility_check", {
    result: params.result,
    gus_success: params.gusSuccess,
  });
};

const trackRegistrationSubmit = (params: {
  planId: string;
  planName: string;
  billingInterval: string;
  activationType: string;
}): void => {
  gtmTrackEvent("registration_submit", {
    plan_id: params.planId,
    plan_name: params.planName,
    billing_interval: params.billingInterval,
    activation_type: params.activationType,
  });
};

const trackRegistrationError = (params: {
  stepNumber: number;
  errorType: RegistrationErrorType;
}): void => {
  gtmTrackEvent("registration_error", {
    step_number: params.stepNumber,
    error_type: params.errorType,
  });
};

const trackSignUp = (params: {
  method: string;
  planId: string;
  useBeacon: boolean;
}): void => {
  // sendBeacon not needed — GTM processes dataLayer events synchronously before unload. The gtmTrackConversion push below is sufficient.
  gtmTrackConversion("sign_up", {
    method: params.method,
    plan_id: params.planId,
  });
};
